import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Nav from "./Navigation.js";
import { getTokenInternal } from "./useToken";

export default function AllListedItems() {
  const [allAccounts, setAllAccounts] = useState([]);
  const [filteredAccounts, setFilteredAccounts] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [token, setToken] = useState();
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [roleFilter, setRoleFilter] = useState("");
  const [privateTesterOnly, setPrivateTesterOnly] = useState(false);

  const navigate = useNavigate();

  const fetchData = async () => {
    const token = await getTokenInternal();
    setToken(token);
    if (
      !token ||
      (token.account.role !== "management" && token.account.role !== "owner")
    ) {
      navigate("/");
      return;
    }

    const url = `${process.env.REACT_APP_API_HOST}/api/accounts`;
    const response = await fetch(url, {
      headers: { Authorization: `Bearer ${token.access_token}` },
    });
    if (!response.ok) return;
    const completeList = await response.json();
    setAllAccounts(completeList);
    setFilteredAccounts(completeList);
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    const filtered = allAccounts.filter((account) =>
      account.username.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredAccounts(filtered);
  }, [searchTerm, allAccounts]);

  useEffect(() => {
    if (!sortConfig.key) return;

    const sorted = [...filteredAccounts].sort((a, b) => {
      const aVal = a[sortConfig.key]?.toString().toLowerCase() || "";
      const bVal = b[sortConfig.key]?.toString().toLowerCase() || "";

      if (aVal < bVal) return sortConfig.direction === "asc" ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });

    setFilteredAccounts(sorted);
  }, [sortConfig]);

  const requestSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const handleEditAccount = (account) => {
    navigate(`/edit-account/${account.id}`, {
      state: { account },
    });
  };

  const handleDeleteAccount = async (account) => {
    try {
      const url = `${process.env.REACT_APP_API_HOST}/api/accounts/${encodeURIComponent(account.username)}`;
      const response = await fetch(url, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token?.access_token}`,
          "Content-Type": "application/json",
        },
      });

      if (response.ok) {
        const updatedAccounts = allAccounts.filter(
          (acc) => acc.id !== account.id
        );
        setFilteredAccounts(updatedAccounts);
      } else {
        console.error("Failed to delete account:", response.statusText);
      }
    } catch (error) {
      console.error("Error deleting account:", error);
    }
  };

  useEffect(() => {
    let filtered = allAccounts;

    if (searchTerm) {
      filtered = filtered.filter((account) =>
        account.username.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (roleFilter) {
      filtered = filtered.filter((account) => account.role === roleFilter);
    }

    if (privateTesterOnly) {
      filtered = filtered.filter((account) => account.is_private_tester);
    }

    setFilteredAccounts(filtered);
  }, [searchTerm, roleFilter, privateTesterOnly, allAccounts]);


  return (
    <div>
      <Nav />
      <div className="mb-4">
  <label htmlFor="roleFilter" className="block text-sm font-medium text-gray-700">
    Filter by Role:
  </label>
  <select
    id="roleFilter"
    className="mt-1 p-2 border rounded-md w-full"
    value={roleFilter}
    onChange={(e) => setRoleFilter(e.target.value)}
  >
    <option value="">All Roles</option>
    {[...new Set(allAccounts.map((a) => a.role).filter(Boolean))].sort().map((role) => (
      <option key={role} value={role}>{role}</option>
    ))}
  </select>
</div>

      <div className="mb-4">
        <label className="flex items-center gap-2 text-sm font-medium text-gray-700 cursor-pointer">
          <input
            type="checkbox"
            checked={privateTesterOnly}
            onChange={(e) => setPrivateTesterOnly(e.target.checked)}
            className="w-4 h-4"
          />
          Private Testers Only
        </label>
      </div>

      <div className="mb-4">
        <label
          htmlFor="search"
          className="block text-sm font-medium text-gray-700"
        >
          Search:
        </label>
        <input
          type="text"
          id="search"
          className="mt-1 p-2 border rounded-md w-full"
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      <div className="mb-4">
        <strong>Total Accounts: {filteredAccounts.length}</strong>
      </div>

      <table className="min-w-full divide-y divide-gray-200 text-white text-left">
        <thead>
          <tr>
            <th>ID</th>
            <th
              onClick={() => requestSort("username")}
              className="cursor-pointer underline"
            >
              Account Name
              {sortConfig.key === "username" &&
                (sortConfig.direction === "asc" ? " ↑" : " ↓")}
            </th>
            <th>Discord Name</th>
            <th
              onClick={() => requestSort("role")}
              className="cursor-pointer underline"
            >
              Role
              {sortConfig.key === "role" &&
                (sortConfig.direction === "asc" ? " ↑" : " ↓")}
            </th>
            <th>Visible</th>
            <th>Private Tester</th>
            <th>Actions</th>
            <th>Total: {filteredAccounts.length}</th>
          </tr>
        </thead>
        <tbody>
          {filteredAccounts.map((account) => (
            <tr key={account.id}>
              <td>{account.id}</td>
              <td>{account.username}</td>
              <td>{account.discord_name}</td>
              <td>{account.role}</td>
              <td>{account.visible.toString()}</td>
              <td>{account.is_private_tester ? "Yes" : "No"}</td>
              <td>
                <button
                  className="mr-2 p-1 border rounded"
                  onClick={() => handleEditAccount(account)}
                  disabled={
                    token &&
                    token.account.role !== "owner" &&
                    account.role === "owner"
                  }
                >
                  Edit
                </button>
              </td>
              <td>
                <button
                  type="button"
                  className="ml-2 bg-red-500 border-solid border-2 border-white text-white px-1 py-0 rounded"
                  onClick={() => handleDeleteAccount(account)}
                  disabled={
                    token &&
                    token.account.role !== "owner" &&
                    account.role === "owner"
                  }
                >
                  Delete Account
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
